import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { merge, Observable } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';

import { GetActiveOrderQuery } from '../../../common/generated-types';
import { DataService } from '../../providers/data/data.service';
import { NotificationService } from '../../providers/notification/notification.service';
import { StateService } from '../../providers/state/state.service';
import { CHECKOUT_ROUTES } from '../../../checkout/checkout.routes';

import { ADJUST_ITEM_QUANTITY, REMOVE_ITEM_FROM_CART } from './cart-drawer.graphql';
import { ActiveService } from '../../providers/active/active.service';

@Component({
    selector: 'vsf-cart-drawer',
    templateUrl: './cart-drawer.component.html',
    styleUrls: ['./cart-drawer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartDrawerComponent implements OnInit {
    @Input() visible = false;
    @Output() close = new EventEmitter<void>();
    @ViewChild('overlay') private overlayRef: ElementRef<HTMLDivElement>;

    public readonly CHECKOUT_ROUTES = CHECKOUT_ROUTES;

    cart$: Observable<GetActiveOrderQuery['activeOrder']>;
    isEmpty$: Observable<boolean>;

    constructor(private dataService: DataService,
                private stateService: StateService,
                private activeService: ActiveService,
                private notificationService: NotificationService) {}

    ngOnInit() {
        this.cart$ = merge(
            this.stateService.select(state => state.activeOrderId),
            this.stateService.select(state => state.signedIn),
        ).pipe(
            switchMap(() => this.activeService.activeOrder$),
            shareReplay(1),
        );
        this.isEmpty$ = this.cart$.pipe(
            map(cart => !cart || cart.lines.length === 0),
        );
    }

    setQuantity(event: { itemId: string; quantity: number; }) {
        if (0 < event.quantity) {
            this.adjustItemQuantity(event.itemId, event.quantity);
        } else {
            this.removeItem(event.itemId);
        }
    }

    overlayClick(event: MouseEvent) {
        if (event.target === this.overlayRef.nativeElement) {
            this.close.emit();
        }
    }

    private adjustItemQuantity(id: string, qty: number) {
        this.dataService.mutate<{
            adjustOrderLineQuantity: {
                order: { id: string } | null;
                errorMessage: string | null;
            };
        }>(ADJUST_ITEM_QUANTITY, { id, qty })
            .pipe(take(1))
            .subscribe(({ adjustOrderLineQuantity }) => {
                if (adjustOrderLineQuantity.errorMessage) {
                    this.notificationService
                        .error(adjustOrderLineQuantity.errorMessage)
                        .subscribe();
                }
                this.dataService.refetchQueries(['GetActiveOrder']);
            });
    }

    private removeItem(id: string) {
        this.dataService.mutate<{
            adjustOrderLineQuantity: {
                order: { id: string } | null;
                errorMessage: string | null;
            };
        }>(REMOVE_ITEM_FROM_CART, { id })
            .pipe(take(1))
            .subscribe(({ adjustOrderLineQuantity }) => {
                if (adjustOrderLineQuantity.errorMessage) {
                    this.notificationService
                        .error(adjustOrderLineQuantity.errorMessage)
                        .subscribe();
                }
                this.dataService.refetchQueries(['GetActiveOrder']);
            });
    }
}
